const { find, merge } = require('lodash');

class GiftCardAddBaseController {
    constructor($timeout, settings, digitalPayPaymentInstrumentsService, digitalPayFormValidator) {
        this.$timeout = $timeout;
        this.settings = settings;
        this.digitalPayPaymentInstrumentsService = digitalPayPaymentInstrumentsService;
        this.digitalPayFormValidator = digitalPayFormValidator;
    }

    $onInit() {
        this.unableToRetrieveBalanceWarningMessage = this.settings.digitalPayError.giftCardUnableToRetrieveBalanceMessage;
        this.hasTooManyRequestsMessage = this.settings.digitalPayError.giftCardTooManyRequestsMessage;

        this.validGiftCardPinNumberLength = this.settings.digitalPay.giftCardPinLength;
        this.validGiftCardNumberLength = this.settings.digitalPay.giftCardNumberLength;
        this.giftCardNumberValidation = {
            allowedBins: this.allowedBins,
            cardDigitLength: this.validGiftCardNumberLength,
        };

        this.invalidateBalance();
    }

    giftCardCaptureFocusGain(event) {
        if (this.hasRetrievedBalance() || !this.focusLostObject) {
            return;
        }

        const errorMessage = this.digitalPayFormValidator.getGiftCardValidationError(this.focusLostObject, event.target.name);
        this.focusLostInvalidFieldsTimeoutPromise = this.invokeAddCardError(errorMessage);
        // Stop the missing fields message from displaying if focus on form is gained
        this.$timeout.cancel(this.focusLostBlankFieldsTimeoutPromise);
        this.focusLostBlankFieldsTimeoutPromise = undefined;
    }

    giftCardCaptureFocusLost(event) {
        if (this.hasRetrievedBalance()) {
            return;
        }

        const response = this.buildGiftCardCaptureState(event.target.name);
        if (response.allFieldsEmpty && event.target.name !== this.settings.digitalPayGiftCardFields.amount) {
            this.clearValidationState();
            return;
        }

        this.focusLostObject = response;

        const errorMessage = this.digitalPayFormValidator.getGiftCardValidationError(this.focusLostObject);
        this.focusLostInvalidFieldsTimeoutPromise = this.invokeAddCardError(errorMessage);

        // If the user moves away from the form, we display a missing fields message instead.
        // This is cancelled if the user focuses on the form immediately after moving away (e.g. tab from one field to another)
        if (this.hasBlankFields(this.focusLostObject)) {
            this.focusLostBlankFieldsTimeoutPromise = this.invokeAddCardError(this.settings.digitalPayError.giftCardFieldsMissingErrorsMessage);
        }
    }

    /**
     * Invokes card error
     * @overridable
     */
    invokeAddCardError(errorMessage) {
        // Timeout exists here because we want to delay the error message appearing as a workaround for the UI design
        return this.$timeout(() => {
            this.errorMessage = errorMessage;
        }, this.settings.digitalPay.formFocusOutTimeoutMilliseconds);
    }

    hasBlankFields(response) {
        return (response.cardValid === null || response.pinValid === null);
    }

    buildGiftCardCaptureState(fieldName) {
        const cardNumber = this.giftCardAddForm.giftCardNumber;
        const pin = this.giftCardAddForm.giftCardPin;
        return {
            allFieldsEmpty: (!cardNumber.$viewValue || !cardNumber.$viewValue.length) &&
                            (!pin.$viewValue || !pin.$viewValue.length),
            field: fieldName,
            cardValid: (!cardNumber.$viewValue || !cardNumber.$viewValue.length)
                ? null
                : !cardNumber.$invalid,
            pinValid: (!pin.$viewValue || !pin.$viewValue.length)
                ? null
                : !pin.$invalid,
        };
    }

    hasRetrievedBalance() {
        return this.hasValidGiftCardBalance || !this.card.BalanceApiAvailable;
    }

    hasValidBalance() {
        return (this.hasValidGiftCardBalance && this.card.Balance) || !this.card.BalanceApiAvailable;
    }

    setCardValidity(isValid) {
        this.giftCardAddForm.giftCardNumber.$setValidity('card', isValid);
        this.giftCardAddForm.giftCardPin.$setValidity('card', isValid);
    }

    isGiftCardBalanceAmountExist() {
        return parseFloat(this.card.Balance) || parseFloat(this.card.Balance) === 0;
    }

    isBalanceSameAsAvailableAmount() {
        return parseFloat(this.card.Amount) === parseFloat(this.card.Balance);
    }

    /**
     * Gets gift card balance
     * @overridable
     */
    getBalance() {
        this.invalidateBalance();
        this.setCardValidity(true);
        if (this.giftCardAddForm.giftCardNumber.$invalid ||
            this.giftCardAddForm.giftCardPin.$invalid ||
            this.card.CardNumber.length !== this.validGiftCardNumberLength ||
            this.card.Pin.length !== this.validGiftCardPinNumberLength) {
            return;
        }
        this.startLoadingBalance();

        this.digitalPayPaymentInstrumentsService.getUnsavedGiftCardBalances({
            GiftCards: [{
                CardNumber: this.card.CardNumber,
                PinCode: this.card.Pin,
            }],
        }).then((response) => {
            this.hasValidGiftCardBalance = false;
            if (response.Success) {
                const giftCardBalance = find(response.GiftCardBalances, { CardNumber: this.card.CardNumber });
                if (!giftCardBalance) {
                    return;
                }

                merge(this.card, giftCardBalance);
                this.hasValidGiftCardBalance = true;

                if (this.card.ErrorCode === this.settings.digitalPayErrorCodes.giftCardKnownBalanceCheckFailed) {
                    this.card.BalanceApiAvailable = false;
                } else if (this.card.ErrorCode === this.settings.digitalPayErrorCodes.giftCardUnableToRetrieveDetails) {
                    this.errorMessage = this.settings.digitalPayError.giftCardIncorrectDetailsMessage;
                    this.setCardValidity(false);
                } else if (this.card.Expired) {
                    this.errorMessage = this.settings.digitalPayError.giftCardAddExpiredMessage;
                    this.card.Balance = undefined;
                } else if (this.card.Balance === 0) {
                    this.errorMessage = this.settings.digitalPayError.giftCardAddInsufficientFundsMessage;
                } else {
                    this.setCardValidity(true);
                    this.errorMessage = '';
                    this.onGetValidBalance();
                }
            } else if (response.HttpStatusCode === 500) {
                this.card.BalanceApiAvailable = false;
            } else if (response.HttpStatusCode === 429) {
                this.hasTooManyRequests = true;
            }
            this.onBalanceCalled(this.card.ErrorCode === this.settings.digitalPayErrorCodes.giftCardUnableToRetrieveDetails, this.card.Expired, this.card.Balance === 0);
        }).finally(() => {
            this.stopLoadingBalance();
        });
    }

    /**
     * Resets card information and errors
     * @overridable
     */
    invalidateBalance() {
        this.card.BalanceApiAvailable = true;
        this.hasTooManyRequests = false;
        this.card.Balance = undefined;
        this.card.Amount = undefined;
        if (this.hasValidGiftCardBalance || (this.card.Number && this.card.Pin)) {
            this.errorMessage = '';
        }
        this.hasValidGiftCardBalance = false;
    }

    /**
     * Resets validation object and errors
     * @overridable
     */
    clearValidationState() {
        this.focusLostObject = undefined;
        this.errorMessage = '';
        this.resetErrorDisplayTimeouts();
    }

    /**
     * For starting the loading spinner
     * @overridable
     */
    startLoadingBalance() {
    }

    /**
     * For stopping the loading spinner
     * @overridable
     */
    stopLoadingBalance() {
    }

    /**
     * Event when the balance is called
     * @overridable
     */
    onBalanceCalled(isInvalid = false, isExpired = false, isZeroBalance = false) { // eslint-disable-line no-unused-vars
    }

    /**
     * Event when a valid balance is found
     * @overridable
     */
    onGetValidBalance() {
    }

    resetErrorDisplayTimeouts() {
        this.$timeout.cancel(this.focusLostBlankFieldsTimeoutPromise);
        this.$timeout.cancel(this.focusLostInvalidFieldsTimeoutPromise);
        this.focusLostBlankFieldsTimeoutPromise = undefined;
        this.focusLostInvalidFieldsTimeoutPromise = undefined;
    }
}

const giftCardAddBaseComponentDefinition = {
    controller: GiftCardAddBaseController,
    bindings: {
        allowedBins: '<',
    },
};

module.exports = {
    GiftCardAddBaseController,
    giftCardAddBaseComponentDefinition,
};



// WEBPACK FOOTER //
// ./src/wallet/giftcard/gift-card-add-base.component.js